import { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Button, 
  Card, 
  CardContent, 
  CardHeader, 
  Grid, 
  TextField, 
  Typography, 
  Stack,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { client, UomType, Instance } from '../../app/services/api/orderManagementClient';
import { UserContext } from '../../components/shared/useUser';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import useLogError from '../../hooks/useLogError';

const UomTypeForm = () => {
  const { t } = useTranslation('uomTypes');
  const { uomTypeId } = useParams<{ uomTypeId: string }>();
  const navigate = useNavigate();
  const { hasPermissionTo } = useContext(UserContext);
  const { logError, extractErrorMessage } = useLogError();
  const isEdit = Boolean(uomTypeId);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [instances, setInstances] = useState<Instance[]>([]);
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [isSuccess, setIsSuccess] = useState<boolean>(true);

  const [searchParams] = useSearchParams();
  const companyId = searchParams.get('companyId');
  const searchTerm = searchParams.get('searchTerm');

  const [formData, setFormData] = useState<UomType>({
    uomTypeId: 0,
    companyId: 0,
    instanceName: '',
    uomName: '',
    isMeasurement: false,
    isBase: false,
    uomCode: '',
    inventoryUomCode: '',
    isSystemUom: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const fetchInstances = async () => {
    try {
      const result = await client.getInstances({
        pageNumber: 0,
        pageSize: 2147483647,
      });
      
      if (result.success && result.data) {
        setInstances(result.data.items);
      }
    } catch (error) {
      console.error('Error fetching instances:', error);
    }
  };

  const fetchUomType = async () => {
    if (!uomTypeId) return;

    setIsLoading(true);
    try {
      const result = await client.getUomType(uomTypeId);
      if (result.success && result.data) {
        setFormData(result.data);
      }
    } catch (error) {
      console.error('Error fetching UOM type:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInstances();
    if (isEdit) {
      fetchUomType();
    }
  }, [uomTypeId, isEdit]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.uomName.trim()) {
      newErrors.uomName = t('uomNameRequired');
    }

    if (!formData.companyId) {
      newErrors.companyId = t('instanceRequired');
    }

    if (!formData.uomCode.trim()) {
      newErrors.uomCode = t('uomCodeRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof UomType) => (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleInstanceChange = (event: React.SyntheticEvent, value: Instance | null) => {
    setFormData(prev => ({
      ...prev,
      companyId: value?.id || 0,
    }));
    
    if (errors.companyId) {
      setErrors(prev => ({
        ...prev,
        companyId: '',
      }));
    }
  };

  const getUrlParams = (): URLSearchParams => {
    const params = new URLSearchParams();
    if (companyId) params.set('companyId', companyId);
    if (searchTerm) params.set('searchTerm', searchTerm);

    return params;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      if (isEdit) {
        const result = await client.updateUomType(uomTypeId!, formData);
        if (result.success) {
          setMessage(t('updateSuccessMessage'));
          setIsSuccess(true);
          setShowMessage(true);
          setTimeout(() => {
            const params = getUrlParams();
            navigate(`/uom-types?${params.toString()}`);
          }, 500);
        } else if (result.message) {
          setMessage(result.message);
          setIsSuccess(false);
          setShowMessage(true);
        }
      } else {
        const result = await client.createUomType(formData);
        if (result.success) {
          setMessage(t('createSuccessMessage'));
          setIsSuccess(true);
          setShowMessage(true);
          setTimeout(() => {
            const params = getUrlParams();
            navigate(`/uom-types?${params.toString()}`);
          }, 500);
        } else if (result.message) {
          setMessage(result.message);
          setIsSuccess(false);
          setShowMessage(true);
        }
      }
    } catch (error) {
      logError(error);
      const errorMessage = extractErrorMessage(error);
      setMessage(errorMessage || (isEdit ? t('updateErrorMessage') : t('createErrorMessage')));
      setIsSuccess(false);
      setShowMessage(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    const params = getUrlParams();
    navigate(`/uom-types?${params.toString()}`);
  };

  const handleCloseMessage = () => {
    setShowMessage(false);
  };

  const userCanSave = hasPermissionTo([isEdit ? 'UomTypeUpdate' : 'UomTypeCreate', 'ConfigurationAdmin']);

  if (isLoading) {
    return (
      <PageArea>
        <PageTitle>{isEdit ? t('editUomType') : t('createUomType')}</PageTitle>
        <Typography>{t('loading')}</Typography>
      </PageArea>
    );
  }

  return (
    <PageArea>
      <PageTitle>{isEdit ? t('editUomType') : t('createUomType')}</PageTitle>
      
      <Stack spacing={8}>
        {/* Basic UOM Type Information Card */}
        <Card elevation={1}>
          <CardHeader
            title={t('basicInformation')}
            sx={{ color: 'text.primary' }}
          />
          <CardContent sx={{ p: 8 }}>
            <Grid 
              container 
              spacing={8}>
              <Grid 
                item 
                xs={12} 
                md={6}>
                <TextField
                  fullWidth
                  label={t('uomName')}
                  placeholder={t('uomNamePlaceholder')}
                  value={formData.uomName}
                  onChange={handleInputChange('uomName')}
                  error={!!errors.uomName}
                  helperText={errors.uomName}
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              <Grid 
                item 
                xs={12} 
                md={6}>
                <TextField
                  fullWidth
                  label={t('uomCode')}
                  placeholder={t('uomCodePlaceholder')}
                  value={formData.uomCode}
                  onChange={handleInputChange('uomCode')}
                  error={!!errors.uomCode}
                  helperText={errors.uomCode}
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              <Grid 
                item 
                xs={12}>
                <Autocomplete
                  options={instances}
                  getOptionLabel={(option) => option.name}
                  value={instances.find(inst => inst.id === formData.companyId) || null}
                  onChange={handleInstanceChange}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('instance')}
                      placeholder={t('instancePlaceholder')}
                      error={!!errors.companyId}
                      helperText={errors.companyId}
                      required
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                  noOptionsText={t('noInstancesFound')}
                  clearOnBlur
                  selectOnFocus
                  handleHomeEndKeys
                  sx={{
                    '& .MuiAutocomplete-inputRoot': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid 
                item 
                xs={12} 
                md={6}>
                <TextField
                  fullWidth
                  label={t('inventoryUomCode')}
                  placeholder={t('inventoryUomCodePlaceholder')}
                  value={formData.inventoryUomCode || ''}
                  onChange={handleInputChange('inventoryUomCode')}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* UOM Type Settings Card */}
        <Card elevation={1}>
          <CardHeader
            title={t('uomTypeSettings')}
            sx={{ color: 'text.primary' }}
          />
          <CardContent sx={{ p: 8 }}>
            <Grid 
              container 
              spacing={4}>
              <Grid 
                item 
                xs={12} 
                md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isMeasurement || false}
                      onChange={handleInputChange('isMeasurement')}
                      color="primary"
                    />
                  }
                  label={t('isMeasurement')}
                />
              </Grid>

              <Grid 
                item 
                xs={12} 
                md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isBase || false}
                      onChange={handleInputChange('isBase')}
                      color="primary"
                    />
                  }
                  label={t('isBase')}
                />
              </Grid>

              <Grid 
                item 
                xs={12} 
                md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isSystemUom || false}
                      onChange={handleInputChange('isSystemUom')}
                      color="primary"
                    />
                  }
                  label={t('isSystemUom')}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Action Buttons Card */}
        <Card
          elevation={0}
          sx={{ backgroundColor: 'background.default' }}>
          <CardContent sx={{ p: 3 }}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="flex-end"
            >
              <Button
                variant="secondary"
                size="lg"
                onClick={handleCancel}
                disabled={isSaving}
              >
                {t('cancel')}
              </Button>
              
              {userCanSave && (
                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleSubmit}
                  disabled={isSaving}
                >
                  {isSaving ? t('saving') : (isEdit ? t('update') : t('create'))}
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showMessage}
        onClose={handleCloseMessage}
        autoHideDuration={5000}
      >
        <Alert
          onClose={handleCloseMessage}
          severity={isSuccess ? 'success' : 'error'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </Snackbar>
    </PageArea>
  );
};

export default UomTypeForm;
